import { Form } from "antd/es";
import type { FormInstance } from "antd/es/form";
import React from "react";
import { KMainFunctions } from "../shared/utilty/main-functions";

declare let window: any;

interface IMasterFormContextValue {
  form: FormInstance | null;
}

export const MasterFormContext = React.createContext<IMasterFormContextValue>({ form: null });

interface IMasterFormBridgeProps {
  children: React.ReactNode;
}

const MasterFormBridge: React.FC<IMasterFormBridgeProps> = ({ children }) => {
  const [form] = Form.useForm();
  return <MasterFormContext.Provider value={{ form }}>{children}</MasterFormContext.Provider>;
};

interface IMasterScreenWrapperLocationState {
  screenInputs?: Record<string, unknown>;
}

interface IMasterScreenWrapperLocation {
  search?: string;
  pathname?: string;
  state?: IMasterScreenWrapperLocationState;
}

// KOA-8559: "Open in Current Overlay" navigation. An inline-mounted master wrapper
// (drawer/modal/floatingPanel) publishes this handle through InlineOverlayNavContext,
// so ONLY the screens rendered inside that overlay's React subtree (its master menu
// and child screen) can reach it. A NAVIGATE action with openAs="currentOverlay" reads
// it from the triggering screen's props (injected by the withInlineOverlayNav HOC) and,
// when the overlay's master can render the target, swaps the overlay's visible child to
// it — keeping the overlay open and the master chrome/shared form mounted — instead of
// routing the page behind the overlay. Triggers outside any overlay get the default
// (null) context, so they degrade to a normal currentPage navigate.
export interface IInlineOverlayNav {
  // True when `screenName` (the NAVIGATE `toScreen`, i.e. the target screen's Name) is
  // one of this overlay master's child screens, so this overlay can render it.
  canRender: (screenName: string) => boolean;
  // Push a screen to render inside the overlay (replaces the visible child; the
  // master/provider/form stay mounted).
  push: (screenName: string, screenInputs: Record<string, unknown>) => void;
}

// Default null: a screen rendered OUTSIDE any inline overlay (router-level, or the page
// behind a maskless FloatPanel) reads null and its currentOverlay navigate degrades to
// currentPage. Only the inline overlay wrapper provides a non-null value.
export const InlineOverlayNavContext = React.createContext<IInlineOverlayNav | null>(null);

interface IInlineOverlayEntry {
  screenName: string;
  screenInputs: Record<string, unknown>;
}

interface IInlineFormScopeProps {
  children?: React.ReactNode;
}

// KOA-17353: form-scope isolation for drawer / modal / floatingPanel child screens that
// have NO master assigned. Such children would otherwise inherit the app-level
// MasterFormContext (set up by the root MasterScreenWrapper), which means their
// <Form.Item> values bind to the shared root form. Antd's <Form> defaults to
// preserve={true}, so unmounting fields on close keeps their values in the shared form;
// the next open re-mounts the fields and rehydrates the stale values. Result: drawer
// looked clean visually but the form state never reset without a page reload.
//
// Wrapping the master-less child in InlineFormScope gives it its own form instance.
// destroyOnClose unmounts the scope along with the child, disposing the form. The next
// open creates a fresh scope and a fresh form.
//
// Children that DO have a master are already isolated by `*_InlineWrapper` (itself a
// MasterScreenWrapper subclass that mounts its own MasterFormBridge), so they don't
// need this wrapper.
export const InlineFormScope: React.FC<IInlineFormScopeProps> = ({ children }) => {
  const [form] = Form.useForm();
  return <MasterFormContext.Provider value={{ form }}>{children}</MasterFormContext.Provider>;
};

interface IMasterScreenWrapperProps {
  children?: React.ReactNode;
  // KOA-17357: react-router-dom injects `location` into the routed `*_Wrapper` subclass when
  // it is mounted via `<Route component={...} />`. We forward it to the Master component so
  // its outer `withScreenInput` HOC can parse query/state into `props.screenInputs`. Without
  // this, screen inputs declared on the master page were always `{}` because `withScreenInput`
  // sits OUTSIDE `withHistory` in the master's HOC chain and runs before the `useLocation`
  // hook injects a location prop. Used by the currentPage / newTab / newWindow openAs paths.
  // Absent for inline-mounted (drawer/modal/floatingPanel) wrappers — they have no Route.
  location?: IMasterScreenWrapperLocation;
  // KOA-17357: for drawer/modal/floatingPanel (and any other non-currentPage / non-newTab /
  // non-newWindow openAs) the generated parent screen passes `window.kuika.navigationScreenInputs`
  // to the `*_InlineWrapper` so the master's `withScreenInput` HOC can populate via the
  // `props.screenInputs` source (the same channel the child screen uses). Undefined for
  // route-mounted wrappers; their screen inputs flow through `location` above.
  screenInputs?: Record<string, unknown>;
}

interface IMasterScreenWrapperState {
  privateRouteReady: boolean;
  // KOA-8559: stack of screens pushed into this overlay via openAs="currentOverlay".
  // Empty for the initial mount (renderSwitchWrapper / props.children is shown) and
  // for every router-level wrapper (which is never an overlay host). The top entry is
  // the screen currently rendered inside the overlay.
  overlayStack: IInlineOverlayEntry[];
}

export abstract class MasterScreenWrapper extends React.PureComponent<
  IMasterScreenWrapperProps,
  IMasterScreenWrapperState
> {
  private _handler: (() => void) | null = null;

  private _inlineOverlayNavValue: IInlineOverlayNav | null = null;

  constructor(props: IMasterScreenWrapperProps) {
    super(props);
    this.state = {
      privateRouteReady: window.userInfo !== undefined,
      overlayStack: []
    };
  }

  protected abstract get anonymousPaths(): string[];
  protected abstract get MasterComponent(): React.ComponentType<any>;
  protected abstract get MasterProvider(): React.ComponentType<any>;
  protected abstract renderSwitchWrapper(): React.ReactNode;

  // KOA-8559: lowercased Names of the screens this wrapper can render inside the
  // overlay via openAs="currentOverlay" (the master's child screens). Returns null for
  // router-level wrappers — null means "not an overlay host", so they don't publish the
  // nav context and their behavior is unchanged. Inline-mount subclasses
  // (drawer/modal/floatingPanel) override this with the generated list of their children.
  protected get overlayScreenNames(): string[] | null {
    return null;
  }

  // KOA-8559: render the screen `screenName` inside the overlay (wrapped by the same
  // master, since this runs inside the wrapper's master tree). Overridden by the
  // generated inline wrapper with a dispatcher over the master's child screens.
  // `onClose` pops the overlay stack back one level.
  protected renderOverlayScreen(
    _screenName: string,
    _screenInputs: Record<string, unknown>,
    _onClose: () => void
  ): React.ReactNode {
    return null;
  }

  private get isInlineOverlayHost(): boolean {
    return this.overlayScreenNames !== null;
  }

  // Stable, lazily-built context value so consumers (the master menu / child screens
  // inside the overlay) don't re-render on unrelated wrapper updates.
  private get inlineOverlayNavValue(): IInlineOverlayNav {
    if (!this._inlineOverlayNavValue) {
      this._inlineOverlayNavValue = {
        canRender: this.canRenderOverlayScreen,
        push: this.pushOverlayScreen
      };
    }
    return this._inlineOverlayNavValue;
  }

  // When true, render master immediately and skip the privateRouteReady auth gate.
  // The router-level wrapper leaves this false (default) so master chrome doesn't flash
  // before login resolves. Inline-mount subclasses (drawer/modal/floatingPanel) override
  // to true: by definition they only mount inside an already-routed (already-authed)
  // parent screen, so the auth gate is meaningless and can incorrectly suppress master
  // when window.userInfo isn't readable at construction time for any reason.
  protected get forceReady(): boolean {
    return false;
  }

  componentDidMount() {
    if (this.state.privateRouteReady) return;
    this._handler = () => this.setState({ privateRouteReady: true });
    window.addEventListener("privateRouteContextReady", this._handler);
  }

  componentWillUnmount() {
    if (this._handler) {
      window.removeEventListener("privateRouteContextReady", this._handler);
      this._handler = null;
    }
  }

  private canRenderOverlayScreen = (screenName: string): boolean => {
    const names: string[] | null = this.overlayScreenNames;
    if (!names || !screenName) return false;
    return names.includes(screenName.toLowerCase());
  };

  private pushOverlayScreen = (screenName: string, screenInputs: Record<string, unknown>): void => {
    this.setState((prev) => ({
      overlayStack: [...prev.overlayStack, { screenName, screenInputs }]
    }));
  };

  private popOverlayScreen = (): void => {
    this.setState((prev) => ({
      overlayStack: prev.overlayStack.slice(0, -1)
    }));
  };

  // KOA-8559: the content rendered inside the master. When a screen has been pushed via
  // openAs="currentOverlay", render that screen (top of the overlay stack); otherwise
  // render the normal switch/children. Router-level wrappers are never overlay hosts, so
  // this always returns renderSwitchWrapper() for them.
  private getOverlayAwareContent(): React.ReactNode {
    const stack: IInlineOverlayEntry[] = this.state.overlayStack;
    if (this.isInlineOverlayHost && stack.length > 0) {
      const top: IInlineOverlayEntry = stack[stack.length - 1];
      return this.renderOverlayScreen(top.screenName, top.screenInputs, this.popOverlayScreen);
    }
    return this.renderSwitchWrapper();
  }

  private isPathAnonymous(): boolean {
    const currentPath = window.location.hash
      ? window.location.hash.replace(/^#/, "").split("?")[0]
      : (() => {
          const virtualDir = KMainFunctions.virtualDirectoryURL === "auto" ? "" : KMainFunctions.virtualDirectoryURL;
          return window.location.pathname.replace(virtualDir, "").split("?")[0];
        })();
    return this.anonymousPaths.some((p) => currentPath?.toLowerCase() === p);
  }

  render() {
    const { privateRouteReady } = this.state;
    const switchContent = this.getOverlayAwareContent();

    if (!this.forceReady && !privateRouteReady && !this.isPathAnonymous()) {
      return switchContent;
    }

    const Master = this.MasterComponent;
    const Provider = this.MasterProvider;

    const masterTree = (
      <MasterFormBridge>
        <Provider>
          <Master location={this.props.location} screenInputs={this.props.screenInputs}>
            {switchContent}
          </Master>
        </Provider>
      </MasterFormBridge>
    );

    // KOA-8559: only the inline overlay wrapper publishes the overlay-nav handle, and
    // only to its own React subtree (master menu + child screen). A currentOverlay
    // navigate triggered from the page behind — or a maskless FloatPanel background —
    // reads the default null context and degrades to a normal currentPage navigate.
    if (this.isInlineOverlayHost) {
      return (
        <InlineOverlayNavContext.Provider value={this.inlineOverlayNavValue}>
          {masterTree}
        </InlineOverlayNavContext.Provider>
      );
    }

    return masterTree;
  }
}
